import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppBar, Box, Button, Container, Toolbar, Typography } from '@mui/material';
import { clearPortalToken, getPortalToken, portalErrorMessage } from '../portalHttp';
import { getPortalParty, SupplierMe, SupplierPortalApi } from './supplierPortalApi';
import SupplierPos, { SupplierPoDetailPage } from './SupplierPos';
import SupplierBillRequests from './SupplierBillRequests';

// ---- F8 supplier portal: its own layout under /portal/{code}/supplier/* (no staff header, no customer nav) ----
export const SupplierSessionContext = createContext<{ me: SupplierMe | null; code: string }>({ me: null, code: '' });
export const useSupplierSession = () => useContext(SupplierSessionContext);

const NotFound: React.FC = () => <Typography variant="h5">Not found</Typography>;

const SupplierPortalShell: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const hasToken = !!getPortalToken();
  const party = getPortalParty();
  const [me, setMe] = useState<SupplierMe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasToken || party === 'CUSTOMER') return;
    let cancelled = false;
    SupplierPortalApi.me()
      .then(m => { if (!cancelled) setMe(m); })
      .catch(e => { if (!cancelled) setLoadError(portalErrorMessage(e, 'Could not load your account')); });
    return () => { cancelled = true; };
  }, [hasToken, party, code]);

  const base = `/portal/${encodeURIComponent(code)}`;
  if (!hasToken) return <Navigate to={`${base}/login`} replace />;
  if (party === 'CUSTOMER') return <Navigate to={base} replace />;

  const sbase = `${base}/supplier`;
  const logout = async () => {
    try { await SupplierPortalApi.logout(); } catch { /* the session may already be gone */ }
    clearPortalToken();
    navigate(`${base}/login`, { replace: true });
  };
  const nav: Array<[string, string]> = [[sbase, 'Purchase orders'], [`${sbase}/bill-requests`, 'Bill requests']];

  return (
    <SupplierSessionContext.Provider value={{ me, code }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 180 }}>
            <Typography variant="subtitle1" fontWeight="bold">{me?.institutionName ?? ' '}</Typography>
            <Typography variant="caption" color="text.secondary">
              {me ? `Supplier portal - ${me.contactName} - ${me.supplierName}` : 'Supplier portal'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {nav.map(([to, label]) => {
              const active = to === sbase
                ? location.pathname === sbase || location.pathname.startsWith(`${sbase}/pos`)
                : location.pathname.startsWith(to);
              return <Button key={label} component={RouterLink} to={to} size="small" variant={active ? 'contained' : 'text'}>{label}</Button>;
            })}
            <Button size="small" color="inherit" onClick={logout}>Logout</Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loadError ? <Typography color="error">{loadError}</Typography> : (
          <Routes>
            <Route index element={<SupplierPos />} />
            <Route path="pos/:id" element={<SupplierPoDetailPage />} />
            <Route path="bill-requests" element={<SupplierBillRequests />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </Container>
    </SupplierSessionContext.Provider>
  );
};

export default SupplierPortalShell;
// ---- end F8 ----
